import { createContext, createElement, useMemo, type ReactNode } from "react";
import type { RowData } from "@tanstack/react-table";
import type { ViewTableRow } from "./core/table";

/**
 * Lets rows be dragged out of the table, onto a drop zone the app registers
 * in an enclosing dnd-kit `DndContext`. A press anywhere on a row drags it,
 * except on the controls inside it; the payload lands in `active.data.current`.
 */
export interface ViewTableRowDragOut<TData> {
    /** The drag payload for a row, or null when the row can't be dragged. */
    getRowDragData: (
        row: TData,
        state: { isSelected: boolean },
    ) => object | null;
}

interface RowDragOutContextValue {
    tableId: string;
    getRowDragData: (rowId: string) => object | null;
}

export const RowDragOutContext = createContext<RowDragOutContextValue | null>(
    null,
);

/** Gives the table's drag-out rows their drag payloads. */
export function RowDragOutProvider<TData extends RowData>({
    children,
    ...options
}: {
    rowDragOut: ViewTableRowDragOut<TData> | undefined;
    rows: Array<ViewTableRow<TData>>;
    tableId: string;
    children: ReactNode;
}) {
    const value = useRowDragOutContextValue(options);
    return createElement(RowDragOutContext.Provider, { value }, children);
}

function useRowDragOutContextValue<TData extends RowData>({
    rowDragOut,
    rows,
    tableId,
}: {
    rowDragOut: ViewTableRowDragOut<TData> | undefined;
    rows: Array<ViewTableRow<TData>>;
    tableId: string;
}): RowDragOutContextValue | null {
    return useMemo(() => {
        if (!rowDragOut) {
            return null;
        }
        const rowsById = new Map(rows.map((row) => [row.id, row]));
        return {
            tableId,
            getRowDragData: (rowId) => {
                const row = rowsById.get(rowId);
                return row
                    ? rowDragOut.getRowDragData(row.original, {
                          isSelected: row.getIsSelected(),
                      })
                    : null;
            },
        };
    }, [rowDragOut, rows, tableId]);
}
